import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import NumberInput from './NumberInput';
import { parseNumber } from '../utils/numberField';

/**
 * SliderInput - drag it, or type it.
 *
 * A range and a number field over ONE value. Dragging is how you find
 * out what a radius should be; typing is how you say it once you know.
 * Offering only the slider makes 1.2 m a matter of luck on a narrow
 * panel; offering only the field makes every adjustment a negotiation
 * with the keyboard.
 *
 * The typed half is `NumberInput` rather than a bare `<input>`: it
 * already solves the footgun where clearing the last digit snaps the
 * field back to zero (v6.40.0), and that behaviour should not have to be
 * discovered twice.
 *
 * `unit` is rendered, never parsed - the value is always a number.
 *
 * `name` is the ACCESSIBLE name, when it differs from the visible one.
 * Only for controls that already had one: the old height field was
 * captioned "Height (cubes)" and named `Layers`, and keeping that name is
 * what stops a caption change from being a rename in thirty-odd places.
 * New controls should leave it alone and be called one thing. IT NAMES
 * THE TYPED HALF, not the slider: a range cannot be filled - `fill()` on
 * `input[type=range]` does nothing - so the name every caller reaches for
 * has to land on the field that accepts text. The slider takes the
 * visible caption.
 */
export default function SliderInput({
  label,
  name,
  value,
  onChange,
  min = 0,
  max = 10,
  step = 1,
  unit,
  float = false,
  title,
  disabled = false,
  'data-testid': testId,
}) {
  const id = testId || `slider-${String(label).toLowerCase().replace(/\s+/g, '-')}`;
  // A DIV, NOT A LABEL. A <label> wrapping two controls binds itself to
  // the first one, so both halves answer to the same name and every
  // lookup becomes ambiguous.
  //
  // AND THE TWO NAMES MUST NOT CONTAIN ONE ANOTHER. `getByLabel` matches
  // by SUBSTRING, so naming the range "Layers slider" beside a field
  // named "Layers" makes `getByLabel('Layers')` resolve to both. The
  // range takes the visible caption; the field takes the caller's name.
  return (
    <div className={styles.sliderField} title={title}>
      <span className={styles.sliderLabel} id={`${id}-caption`}>
        {label}
      </span>
      <input
        type="range"
        className={styles.sliderTrack}
        aria-labelledby={`${id}-caption`}
        data-testid={id}
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseNumber(e.target.value, float))}
      />
      <NumberInput
        className={clsx(common.input, styles.sliderNumber)}
        aria-label={name || label}
        data-testid={`${id}-value`}
        min={min}
        max={max}
        step={step}
        float={float}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
      {unit ? (
        <span className={styles.sliderUnit} aria-hidden="true">
          {unit}
        </span>
      ) : null}
    </div>
  );
}
